export const calculateEMA = (prices, index, period) => {
  if (index < period - 1) return null;
  let sum = 0;
  for (let i = index - period + 1; i <= index; i++) {
    sum += prices[i];
  }
  let ema = sum / period;
  const multiplier = 2 / (period + 1);
  for (let i = index - period + 2; i <= index; i++) {
    ema = (prices[i] * multiplier) + (ema * (1 - multiplier));
  }
  return ema;
};

export const calculateRSI = (prices, index, period) => {
  if (index <= period) return null;
  let gains = 0;
  let losses = 0;
  for (let i = index - period + 1; i <= index - 1; i++) {
    const change = prices[i] - prices[i - 1];
    if (change > 0) gains += change;
    else losses += Math.abs(change);
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  const currentChange = prices[index] - prices[index - 1];
  const currentGain = currentChange > 0 ? currentChange : 0;
  const currentLoss = currentChange < 0 ? Math.abs(currentChange) : 0;
  avgGain = (avgGain * (period - 1) + currentGain) / period;
  avgLoss = (avgLoss * (period - 1) + currentLoss) / period;
  if (avgLoss === 0) return 100;
  const rs = avgGain / avgLoss;
  return 100 - (100 / (1 + rs));
};

export const runSeasonalityAnalysis = (trades) => {
  const dayStats = {};
  const dateStats = {};
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  trades.filter(t => t.profit !== undefined).forEach(trade => {
    const d = new Date(trade.timestamp);
    const dayName = days[d.getDay()];
    const dateNum = d.getDate();
    if (!dayStats[dayName]) dayStats[dayName] = { profit: 0, count: 0, wins: 0 };
    dayStats[dayName].profit += trade.profit;
    dayStats[dayName].count += 1;
    if (trade.profit > 0) dayStats[dayName].wins += 1;
    
    if (!dateStats[dateNum]) dateStats[dateNum] = { profit: 0, count: 0, wins: 0 };
    dateStats[dateNum].profit += trade.profit;
    dateStats[dateNum].count += 1;
    if (trade.profit > 0) dateStats[dateNum].wins += 1;
  });
  
  let bestDay = { name: "N/A", profit: -Infinity };
  Object.entries(dayStats).forEach(([name, data]) => {
    if (data.profit > bestDay.profit) {
      bestDay = { name, profit: data.profit };
    }
  });
  
  let bestDate = { num: "N/A", profit: -Infinity };
  Object.entries(dateStats).forEach(([num, data]) => {
    if (data.profit > bestDate.profit) {
      bestDate = { num, profit: data.profit };
    }
  });
  return { dayStats, dateStats, bestDay, bestDate };
};

export const getIntervalMinutes = (tf) => {
  const intervals = {
    "1m": 1, "3m": 3, "5m": 5, "15m": 15, "30m": 30,
    "1h": 60, "2h": 120, "4h": 240, "6h": 360, "12h": 720,
    "1d": 1440, "3d": 4320, "1w": 10080,
  };
  return intervals[tf] || 60;
};

export const generateMockHistoricalData = (startDate, endDate, timeframe) => {
  const data = [];
  const startPrice = 50000;
  let currentPrice = startPrice;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const interval = getIntervalMinutes(timeframe);
  
  for (let date = new Date(start); date <= end; date.setMinutes(date.getMinutes() + interval)) {
    const change = (Math.random() - 0.5) * 0.02;
    currentPrice *= 1 + change;
    const high = currentPrice * (1 + Math.random() * 0.01);
    const low = currentPrice * (1 - Math.random() * 0.01);
    const volume = Math.random() * 1000000;
    
    data.push({
      timestamp: date.toISOString(),
      open: currentPrice, high: high, low: low,
      close: currentPrice, volume: volume,
      time: Math.floor(date.getTime() / 1000),
    });
  }
  return data.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};

export const prepareChartData = (data, trades = []) => {
  if (!data || data.length === 0) return null;
  const step = Math.max(1, Math.floor(data.length / 10));
  const labels = data.map((candle, index) => {
    if (index % step === 0 || index === data.length - 1) {
      const date = new Date(candle.timestamp);
      return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " + date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    return "";
  });
  
  const prices = data.map((candle) => candle.close);
  const volumes = data.map((candle) => candle.volume);
  
  const tradeMarkers = trades.map((trade) => {
    const candleIndex = data.findIndex(candle => new Date(candle.timestamp).getTime() >= new Date(trade.timestamp).getTime());
    return { x: candleIndex >= 0 ? candleIndex : 0, y: trade.price, type: trade.type, profit: trade.profit };
  });
  
  return {
    labels: labels,
    datasets: [
      { label: "Price", data: prices, borderColor: "rgb(75, 192, 192)", backgroundColor: "rgba(75, 192, 192, 0.1)", borderWidth: 2, fill: true, tension: 0.1, pointRadius: 0, pointHoverRadius: 5 },
      { label: "Volume", data: volumes, type: "bar", backgroundColor: "rgba(54, 162, 235, 0.2)", borderColor: "rgba(54, 162, 235, 1)", borderWidth: 1, yAxisID: "y1" }
    ],
    tradeMarkers: tradeMarkers,
  };
};

export const calculateMaxConsecutiveWins = (trades) => {
  let maxWins = 0;
  let currentWins = 0;
  trades.forEach((trade) => {
    if (trade.profit > 0) {
      currentWins++;
      maxWins = Math.max(maxWins, currentWins);
    } else {
      currentWins = 0;
    }
  });
  return maxWins;
};

export const calculateMaxConsecutiveLosses = (trades) => {
  let maxLosses = 0;
  let currentLosses = 0;
  trades.forEach((trade) => {
    if (trade.profit < 0) {
      currentLosses++;
      maxLosses = Math.max(maxLosses, currentLosses);
    } else {
      currentLosses = 0;
    }
  });
  return maxLosses;
};
